/*
 * SubscriptionReturn — landing page after Stripe redirects the user back
 * from hosted checkout.
 *
 * URL shape: /subscription/return?session_id=cs_test_...
 *
 * Behaviour:
 *   1. Read session_id from the query string.
 *   2. Poll GET /api/stripe/checkout/status/{session_id} up to N times
 *      until we see `payment_status === 'paid'` (or `session_status`
 *      switches to `expired` / `complete-unpaid`).
 *   3. On success: refresh the local auth user, toast, and redirect to
 *      the Dashboard.
 *   4. On failure/expired: show a calm retry state and a link back to
 *      the paywall.
 *
 * We intentionally poll AND rely on the webhook — either can flip the
 * user's subscription state, and both are idempotent server-side.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const COLORS = {
  bg: '#121212',
  card: '#1C1E21',
  gold: '#D4AF37',
  goldSoft: 'rgba(212, 175, 55, 0.35)',
  alabaster: '#FDFBF7',
  sand: '#D2B48C',
  fgSubtle: 'rgba(253, 251, 247, 0.72)',
  fgMuted: 'rgba(253, 251, 247, 0.52)',
  hairline: 'rgba(253, 251, 247, 0.08)',
};

const MAX_ATTEMPTS = 12;   // ~24s total
const POLL_INTERVAL_MS = 2000;

export default function SubscriptionReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get('session_id');
  const [phase, setPhase] = useState('polling'); // polling | success | failed | timeout
  const [attempt, setAttempt] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setPhase('failed');
      return;
    }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      setAttempt(n);
      try {
        const status = await api.getStripeCheckoutStatus(sessionId);
        if (cancelled) return;

        if (status?.is_paid) {
          setPhase('success');
          toast.success('Subscription active', {
            description: 'Welcome to the covenant of rest.',
          });
          try {
            await refreshUser?.();
          } catch {
            /* no-op */
          }
          setTimeout(() => {
            if (!cancelled) navigate('/dashboard');
          }, 1500);
          return;
        }

        if (
          status?.status === 'expired' ||
          status?.payment_status === 'unpaid' ||
          status?.payment_status === 'failed'
        ) {
          setPhase('failed');
          return;
        }

        if (n >= MAX_ATTEMPTS) {
          setPhase('timeout');
          return;
        }
        timerRef.current = setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (n >= MAX_ATTEMPTS) {
          setPhase('timeout');
          return;
        }
        timerRef.current = setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
      }
    };

    poll(1);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      data-testid="subscription-return-page"
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: COLORS.bg, color: COLORS.alabaster }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8 text-center"
        style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.hairline}`,
          boxShadow: '0 24px 60px -20px rgba(0,0,0,0.55)',
        }}
      >
        {phase === 'polling' && (
          <>
            <Loader2 size={40} className="mx-auto mb-6 animate-spin" style={{ color: COLORS.gold }} />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Confirming your subscription…
            </h1>
            <p className="text-sm" style={{ color: COLORS.fgSubtle }}>
              We&rsquo;re finalising your covenant of rest.
              This usually takes a few seconds.
            </p>
            <p className="text-[11px] mt-6" style={{ color: COLORS.fgMuted }}>
              Attempt {attempt} of {MAX_ATTEMPTS}
            </p>
          </>
        )}

        {phase === 'success' && (
          <>
            <CheckCircle2 size={44} className="mx-auto mb-6" style={{ color: COLORS.gold }} />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Welcome, friend.
            </h1>
            <p className="text-sm mb-6" style={{ color: COLORS.fgSubtle }}>
              Your subscription is active. Taking you to your dashboard…
            </p>
            <p
              className="text-[13px] italic"
              style={{ color: COLORS.sand, fontFamily: '"Crimson Text", serif' }}
            >
              &ldquo;Come to me, all who are weary… and I will give you rest.&rdquo;
            </p>
          </>
        )}

        {phase === 'failed' && (
          <>
            <XCircle size={44} className="mx-auto mb-6" style={{ color: '#c98a8a' }} />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Something interrupted checkout.
            </h1>
            <p className="text-sm mb-6" style={{ color: COLORS.fgSubtle }}>
              No worries — no charge was made. You can try again from the paywall.
            </p>
            <Button
              onClick={() => navigate('/login')}
              data-testid="subscription-return-retry-button"
              className="w-full h-11 rounded-xl font-semibold"
              style={{
                background: `linear-gradient(180deg, ${COLORS.gold} 0%, #B7912D 100%)`,
                color: '#1A1204',
                border: `1px solid ${COLORS.gold}`,
              }}
            >
              Return to sign in
              <ArrowRight size={15} className="ml-1.5" />
            </Button>
          </>
        )}

        {phase === 'timeout' && (
          <>
            <Loader2 size={40} className="mx-auto mb-6" style={{ color: COLORS.gold }} />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Still processing…
            </h1>
            <p className="text-sm mb-6" style={{ color: COLORS.fgSubtle }}>
              Stripe is taking longer than usual to confirm the charge.
              Your subscription will activate as soon as it clears — try
              refreshing your dashboard in a minute or two.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => window.location.reload()}
                data-testid="subscription-return-refresh-button"
                variant="outline"
                className="flex-1 h-10 rounded-xl"
                style={{
                  background: 'transparent',
                  color: COLORS.alabaster,
                  border: `1px solid ${COLORS.hairline}`,
                }}
              >
                Retry check
              </Button>
              <Link to="/dashboard" className="flex-1">
                <Button
                  data-testid="subscription-return-dashboard-button"
                  className="w-full h-10 rounded-xl font-semibold"
                  style={{
                    background: `linear-gradient(180deg, ${COLORS.gold} 0%, #B7912D 100%)`,
                    color: '#1A1204',
                    border: `1px solid ${COLORS.gold}`,
                  }}
                >
                  Dashboard
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
